import type { Locale } from "antd/lib/locale-provider";
import enUS from "antd/lib/locale/en_US";
import esES from "antd/lib/locale/es_ES";
import kkKZ from "antd/lib/locale/kk_KZ";
import ruRU from "antd/lib/locale/ru_RU";
// antd 4's DatePicker formats through moment, so every UI language needs its
// moment locale registered before `moment.locale()` can switch to it.
import "moment/locale/es";
import "moment/locale/kk";
import "moment/locale/ru";

/**
 * A language the admin interface itself is translated into.
 *
 * Deliberately separate from `config/locales.ts`: that module configures the
 * locales an admin can *author entity translations for*, which depends on the
 * backend, while this one lists the locales the *chrome* ships bundles for.
 * A deploy target whose backend has no `translations` support still gets a
 * translated interface.
 */
export interface UiLanguage {
    /** i18next language code, also the `<html lang>` value. */
    code: string;
    /** Endonym shown in the language switcher. */
    label: string;
    /** Ant Design's own bundle (table filters, pagination, empty states, ...). */
    antd: Locale;
    /** moment locale id, for the date pickers. */
    moment: string;
}

export const uiLanguages: ReadonlyArray<UiLanguage> = [
    { code: "en", label: "English", antd: enUS, moment: "en" },
    { code: "kk", label: "Қазақша", antd: kkKZ, moment: "kk" },
    { code: "ru", label: "Русский", antd: ruRU, moment: "ru" },
    { code: "es", label: "Español", antd: esES, moment: "es" }
];

export const fallbackUiLanguage: UiLanguage = uiLanguages[0];

/** localStorage key holding the admin's chosen interface language. */
export const UI_LANGUAGE_STORAGE_KEY = "uiLanguage";

/**
 * Resolves an i18next language to a shipped bundle.
 *
 * i18next hands us whatever the detector found, which may be a regional tag
 * (`ru-RU`) or a language we do not ship — both fall back rather than throw.
 */
export function resolveUiLanguage(code: string | undefined): UiLanguage {
    if (code == null) return fallbackUiLanguage;
    const base = code.toLowerCase().split(/[-_]/)[0];
    return uiLanguages.find(language => language.code === base) ?? fallbackUiLanguage;
}
